<template>
  <div class="rms-reservation-trigger">
    <!-- Optional custom button (e.g. header style from generic-templates); slot receives openModal -->
    <slot name="button" :open-modal="openModal">
      <button
        type="button"
        class="rms-reservation-trigger__btn"
        :style="buttonStyle"
        :aria-label="buttonLabel"
        data-testid="reservation-open-modal"
        @click="openModal"
      >
        <span class="material-icons rms-reservation-trigger__icon" aria-hidden="true">event_seat</span>
        {{ buttonLabel }}
      </button>
    </slot>
    <AppModal
      :open="open"
      :title="modalTitle"
      :description="modalTitle"
      test-id="reservation-modal"
      :close-on-backdrop-click="false"
      @close="onClose"
    >
      <PublicReservationForm
        :slug="slug"
        :template="template"
        :primary-color="primaryColor || undefined"
        :pax-label="paxLabel"
      />
    </AppModal>
  </div>
</template>

<script setup>
import { ref, computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import AppModal from '@/components/ui/AppModal.vue'
import PublicReservationForm from '@/components/public/PublicReservationForm.vue'

const { t } = useI18n()

const props = defineProps({
  slug: { type: String, required: true },
  template: { type: String, default: 'template-1' },
  primaryColor: { type: String, default: null },
  /** Override for CTA button and aria (e.g. "Book" for services, "Book a Table" for restaurants). */
  buttonLabel: { type: String, default: '' },
  /** Override for modal title (e.g. "Book" for services, "Book a table" for restaurants). */
  modalTitle: { type: String, default: '' },
  /** Override for pax field label (e.g. "Party size" for services, "Number of guests" for restaurants). */
  paxLabel: { type: String, default: '' },
})

const buttonLabel = computed(() => props.buttonLabel || t('reservation.reserveButton'))
const modalTitle = computed(() => props.modalTitle || t('reservation.heading'))

/** When set, only one of menu or reservation modal may be open (from PublicRestaurantView). */
const activePublicModal = inject('activePublicModal', null)

const open = ref(false)

function openModal() {
  if (activePublicModal && activePublicModal.value === 'menu') return
  if (activePublicModal) activePublicModal.value = 'reservation'
  open.value = true
}

function onClose() {
  if (activePublicModal && activePublicModal.value === 'reservation') activePublicModal.value = null
  open.value = false
}

const buttonStyle = computed(() => (props.primaryColor ? { backgroundColor: props.primaryColor, borderColor: props.primaryColor } : {}))
</script>

<style scoped>
.rms-reservation-trigger__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  background-color: var(--rms-primary, #0d9488);
  border: 2px solid var(--rms-primary, #0d9488);
  border-radius: 0.5rem;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
}
.rms-reservation-trigger__btn:hover {
  opacity: 0.95;
}
.rms-reservation-trigger__btn:active {
  transform: scale(0.98);
}
.rms-reservation-trigger__btn:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}
.rms-reservation-trigger__icon {
  font-size: 1.25rem;
}
</style>
